<script setup lang="ts">
import Quill from "quill";
import { useWordProcessorStore } from "../useWordProcessorStore";

const wordProcessorInstanceId = inject("instanceId") as string;
const wordProcessorStore = useWordProcessorStore(wordProcessorInstanceId);
const { selectionRange } = storeToRefs(wordProcessorStore);

const editorRef = inject("quillEditor") as any;

const activeHighlightColor = computed(() => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return "";

  const selection = selectionRange.value;
  if (!selection) return "";

  const format = quill.getFormat(selection.index, selection.length);
  return format.background || "";
});

// Helper function to convert hex color to rgba with transparency
const hexToRgba = (hex: string, alpha: number) => {
  if (!hex) return "";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const onHighlightColorSelected = (color: string) => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  const selection = quill.getSelection();

  if (!selection) return;

  quill.formatText(
    selection.index,
    selection.length,
    "background",
    color,
    Quill.sources.USER
  );
};
</script>

<template>
  <!-- Highlight Color Picker -->
  <BasePopover align="start" trigger-class="!w-[30px] !h-[30px] !p-0 rounded">
    <div
      v-tooltip="'Highlight Color'"
      class="w-[30px] h-[30px] rounded hover:bg-light flex items-center justify-center relative"
      :style="{
        backgroundColor: hexToRgba(activeHighlightColor, 0.2),
      }"
    >
      <icon name="lucide:highlighter" size="18" />
    </div>
    <template #content>
      <div class="p-2 w-[200px]">
        <div class="font-bold border-b pb-1 mb-2">Highlight Color</div>
        <div class="grid grid-cols-7 gap-2">
          <button
            v-for="color in [
              'transparent',
              '#ffff00',
              '#00ff00',
              '#00ffff',
              '#ff00ff',
              '#0000ff',
              '#ff0000',
              '#00ff00',
              '#ffcc00',
              '#ff9900',
              '#ff6600',
              '#ff0066',
              '#cc00ff',
              '#6600ff',
              '#0066ff',
              '#00ccff',
              '#00ff66',
              '#66ff00',
              '#ccff00',
              '#ffff66',
              '#ffcc99',
            ]"
            :key="color"
            class="w-4 h-4 rounded-md border hover:scale-110 transition-transform cursor-pointer flex flex-col items-center justify-center"
            :class="{
              ' ring-4 ring-primary/30 shadow-lg shadow-primary/50 ':
                activeHighlightColor === color,
              'border-gray-300': activeHighlightColor !== color,
            }"
            :style="{
              backgroundColor: color === 'transparent' ? '#ffffff' : color,
              border: color === 'transparent' ? '2px solid #ef4444' : '',
            }"
            @click="onHighlightColorSelected(color)"
          >
            <icon
              v-if="color === 'transparent'"
              name="lucide:x"
              size="16"
              class="text-red-500"
            />
          </button>
        </div>
      </div>
    </template>
  </BasePopover>
</template>

<style scoped></style>
